import { PageController } from '@client/components/_core/layout/PageController';
import { VDatePicker } from '@client/components/_shared/forms/view-form/VDatePicker';
import { VDropdown } from '@client/components/_shared/forms/view-form/VDropdown';
import { VHandlingPartySelect } from '@client/components/_shared/forms/view-form/VHandlingPartySelect';
import { VReadOnly } from '@client/components/_shared/forms/view-form/VReadOnly';
import { VTextInput } from '@client/components/_shared/forms/view-form/VTextInput';
import { VersionSubmissionsCard } from '@client/components/lodgement/submissions/VersionSubmissionsCard';
import { LodgementCopyCard } from '@client/components/lodgement/versions/LodgementCopyCard';
import { NeededToPublish, usePublishReadiness } from '@client/components/products-v2/PublishReadiness';
import { productVersionFormStore } from '@client/components/products-v2/store';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { CoverageBasis } from '@shared/prisma/enums';
import { createFileRoute } from '@tanstack/react-router';
import { Card } from 'antd';

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/products-v2/details/$productId/product-versions/details/$productVersionId/details',
)({
    staticData: {
        ui: {
            useParentTitle: false,
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <Details />
        </PageController>
    );
}

const COVERAGE_BASIS_LABELS: Record<CoverageBasis, string> = {
    OCCURRENCE: 'Occurrence',
    CLAIMS_MADE: 'Claims-made',
    CLAIMS_MADE_AND_NOTIFIED: 'Claims-made and notified',
};

function Details() {
    const { formatDate } = useFormatter();
    const { trpcUtils } = useTrpc();
    const { productId, productVersionId } = Route.useParams();
    // A published version is read-only, and only a draft is checked for publishing.
    const isPublished = productVersionFormStore.use.isReadonly();
    const { check } = usePublishReadiness(productVersionId, !isPublished);

    return (
        <div className="grid items-start gap-4 xl:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-4">
                <Card
                    size="small"
                    title="Version details"
                    classNames={{
                        body: 'space-y-4',
                    }}
                >
                    <VTextInput label="Name" field="name" formStore={productVersionFormStore} />
                    <VDropdown
                        label="Coverage basis"
                        field="coverageBasis"
                        formStore={productVersionFormStore}
                        allowClear
                        options={Object.entries(COVERAGE_BASIS_LABELS).map(([value, label]) => ({ value, label }))}
                    />
                    <div className="grid grid-cols-2 gap-4">
                        <VDatePicker
                            label="Launch date"
                            field="startDate"
                            formStore={productVersionFormStore}
                            showTime={{ format: 'HH:mm' }}
                        />
                        <VReadOnly
                            label="End date"
                            field="endDate"
                            formStore={productVersionFormStore}
                            tooltip="Auto-populated when the next version is published, or set manually if the product ends."
                            formatValueFn={(value) => (value ? formatDate(value) : '-')}
                        />
                    </div>
                    <div className="space-y-1">
                        <VDropdown
                            label="Product type"
                            field="productTypeId"
                            formStore={productVersionFormStore}
                            tooltip="Determines the claim states, cost codes, GST settings and communication templates a claim lodged under this version uses. Required before publishing."
                            formatValueFn={(_, { ProductType }) => ProductType?.name}
                            loaderFn={async () => {
                                const data = await trpcUtils.productType.listProductTypes.fetch({
                                    orderBy: { name: 'asc' },
                                });

                                return data.rows.map(({ id, name }) => ({ value: id, label: name }));
                            }}
                        />
                        {/* The one requirement on this card a reader cannot guess from an empty field. */}
                        {check('productType') && <NeededToPublish />}
                    </div>
                    <VHandlingPartySelect
                        label="Handling parties"
                        field="handlingPartyIds"
                        formStore={productVersionFormStore}
                    />
                </Card>
                {/* Links serve the published version, so a draft has no submissions to show. */}
                {isPublished && <VersionSubmissionsCard productId={productId} productVersionId={productVersionId} />}
            </div>
            <LodgementCopyCard productVersionId={productVersionId} />
        </div>
    );
}
